import { useGetComplianceItem } from '@commenda/api';
import { getClientInfoFromCookie } from '@commenda/auth';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  IconButton,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { SyntheticComplianceStatus } from '@commenda/types/src/compliance-calendar/compliances/types';
import { AppType, classNames, cn, useApp } from '@commenda/utils';
import { ComplianceManagedBy, ComplianceStatus } from '@prisma/client';
import React, { useState } from 'react';
import ArchiveFilingModal from '../Filings/modals/ArchiveFilingModal';
import ExtendFilingModal from '../Filings/modals/ExtendFilingModal';
import RequestExtensionModal from '../Filings/modals/RequestExtensionModal';
import { AgendaMode } from './AgendaDateGroup';
import {
  CALENDAR_SUBTITLE_DOT,
  CALENDAR_TEXT_PRIMARY,
  CALENDAR_TEXT_SECONDARY,
  CATEGORY_COLOR,
  CATEGORY_COLOR_FALLBACK,
  CATEGORY_LABEL,
  STATUS_BADGE_VARIANT,
  STATUS_LABEL,
} from './calendar.constants';
import { RbcEvent } from './calendar.types';

const SubtitleDot = () => (
  <div
    className="shrink-0 rounded-full w-1 h-1"
    style={{ backgroundColor: CALENDAR_SUBTITLE_DOT }}
  />
);

interface AgendaEventCardProps {
  event: RbcEvent;
  onSelect: () => void;
  /** When omitted defaults to 'horizontal'. 'stacked' intentionally hides the
   * per-row action menu. */
  mode?: AgendaMode;
  /** Invoked after a successful per-row mutation so the parent can refetch. */
  onFilingMutated?: () => void;
}

const AgendaEventCard = ({
  event,
  onSelect,
  mode = 'horizontal',
  onFilingMutated,
}: AgendaEventCardProps) => {
  const [hovered, setHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const archiveFilingModalVisibility = useToggle();
  const extendFilingModalVisibility = useToggle();
  const requestExtensionModalVisibility = useToggle();
  const { appType } = useApp();
  const {
    syntheticStatus,
    category,
    corporationName,
    state,
    title,
    assigneeName,
    source,
  } = event.resource;

  // categoryColor passes through `style` because its hex values come from
  // enum-keyed maps that Tailwind's JIT can't precompile.
  const categoryColor = CATEGORY_COLOR[category] ?? CATEGORY_COLOR_FALLBACK;
  const badgeVariant = STATUS_BADGE_VARIANT[syntheticStatus] ?? 'gray';
  const statusLabel = STATUS_LABEL[syntheticStatus] ?? syntheticStatus;
  const categoryLabel = CATEGORY_LABEL[category] ?? category;

  const subtitleParts = [corporationName, state, categoryLabel].filter(
    Boolean
  ) as string[];

  // Extension gating mirrors FilingDropdown / FilingPageHeader — see those
  // files for the full matrix. `mode === 'stacked'` stays glance-only.
  // Sales-tax events live in the same view but don't
  // map to a ComplianceItem, so the extend flow doesn't apply to them.
  const isExtensionLaunched = isFeatureLaunched(
    FeatureLaunch.COMPLIANCE_EXTENSION
  );
  const showExtensionActions =
    isExtensionLaunched && mode !== 'stacked' && source === 'COMPLIANCE_ITEM';
  // Calendar events omit ownership and firm assignment. Load the filing only
  // while its actions are in use, and keep that query alive while a modal is open.
  const shouldLoadCompliance =
    showExtensionActions &&
    (dropdownOpen ||
      extendFilingModalVisibility.visible ||
      requestExtensionModalVisibility.visible);
  const {
    data: complianceData,
    error: complianceError,
    mutate: refreshCompliance,
  } = useGetComplianceItem(shouldLoadCompliance ? event.resource.id : '');
  const compliance =
    !complianceError && complianceData?.compliance?.id === event.resource.id
      ? complianceData.compliance
      : undefined;
  const isLoadingCompliance =
    shouldLoadCompliance && !complianceData && !complianceError;
  const isFilingTerminalOrExtended =
    compliance?.status === ComplianceStatus.COMPLETED ||
    compliance?.status === ComplianceStatus.FILED ||
    compliance?.status === ComplianceStatus.ARCHIVED ||
    compliance?.status === ComplianceStatus.EXTENDED;
  const isFilingTerminal =
    syntheticStatus === SyntheticComplianceStatus.COMPLETED ||
    syntheticStatus === SyntheticComplianceStatus.FILED ||
    syntheticStatus === SyntheticComplianceStatus.ARCHIVED;
  const canArchive =
    mode !== 'stacked' && source === 'COMPLIANCE_ITEM' && !isFilingTerminal;
  const hasServiceRequest = Boolean(compliance?.serviceRequestId);
  const isActorApp = appType === AppType.AGENT || appType === AppType.ADMIN;
  const isClientManaged = compliance?.managedBy === ComplianceManagedBy.CLIENT;
  const isCommendaManaged =
    compliance?.managedBy === ComplianceManagedBy.COMMENDA;
  const hasAssignedAgentFirm = Boolean(compliance?.assignedAgentFirmId);
  const isCommendaAdmin = Boolean(
    getClientInfoFromCookie()?.user?.isCommendaAdmin
  );

  const canExtendDirectly =
    showExtensionActions &&
    Boolean(compliance?.status) &&
    !isFilingTerminalOrExtended &&
    (isClientManaged ||
      (isCommendaManaged &&
        (hasServiceRequest
          ? hasAssignedAgentFirm && isActorApp
          : isCommendaAdmin)));
  const canRequestExtension =
    showExtensionActions &&
    Boolean(compliance?.status) &&
    !isFilingTerminalOrExtended &&
    isCommendaManaged &&
    (hasServiceRequest ? hasAssignedAgentFirm : !isCommendaAdmin) &&
    !isActorApp;
  const dropdownItems = [
    isLoadingCompliance && {
      label: 'Loading filing actions...',
      disabled: true,
    },
    showExtensionActions &&
      complianceError && {
        label: 'Retry loading filing actions',
        icon: Icons.RefreshCw01,
        onClick: () => refreshCompliance().catch(() => undefined),
      },
    canExtendDirectly && {
      label: 'Extend filing',
      icon: Icons.Calendar,
      onClick: (e?: React.MouseEvent) => {
        e?.stopPropagation();
        extendFilingModalVisibility.show();
      },
    },
    canRequestExtension && {
      label: 'Request for extension',
      icon: Icons.Calendar,
      onClick: (e?: React.MouseEvent) => {
        e?.stopPropagation();
        requestExtensionModalVisibility.show();
      },
    },
    canArchive && {
      label: 'Archive filing',
      icon: Icons.Archive,
      onClick: (e?: React.MouseEvent) => {
        e?.stopPropagation();
        archiveFilingModalVisibility.show();
      },
      labelClassName: 'text-utility-error-600',
      iconClassName: 'text-utility-error-600',
    },
  ].filter(Boolean);
  const hasDropdownActions = dropdownItems.length > 0;

  return (
    <div
      className={classNames(
        'flex items-start justify-center w-full pl-2 pr-3 py-2 rounded-md cursor-pointer transition-colors duration-150 motion-reduce:transition-none',
        hovered || dropdownOpen ? 'bg-[#f8f6f1]' : undefined
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
      data-ai-item="Filing"
      data-ai-id={event.id}
    >
      <div className="flex flex-1 min-w-0 items-center">
        <div className="flex flex-1 items-stretch min-w-0 gap-3">
          <div
            className="shrink-0 self-stretch rounded-full w-1"
            style={{ backgroundColor: categoryColor }}
          />

          <div className="flex flex-1 flex-col min-w-0 gap-1">
            <div className="flex items-center gap-2.5 min-w-0">
              <span
                className="txt-small-bold truncate min-w-0"
                style={{ color: CALENDAR_TEXT_PRIMARY }}
              >
                {title}
              </span>
              <div className="shrink-0">
                <Badge variant={badgeVariant} size="sm">
                  {statusLabel}
                </Badge>
              </div>
            </div>

            <div className="flex items-center min-w-0 gap-1.5">
              {subtitleParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <SubtitleDot />}
                  <span
                    className="txt-extra-small-medium whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{ color: CALENDAR_TEXT_SECONDARY }}
                  >
                    {part}
                  </span>
                </React.Fragment>
              ))}
            </div>

            {assigneeName && (
              <div className="flex items-center max-w-full min-w-0 rounded-full pointer-events-none self-start mt-2 gap-1 py-1 pr-2 pl-1">
                <Avatar
                  size="2xs"
                  className="size-5"
                  name={assigneeName}
                  colorSeed={assigneeName}
                />
                <span
                  className="txt-extra-small-regular truncate min-w-0"
                  style={{ color: CALENDAR_TEXT_PRIMARY }}
                >
                  {assigneeName}
                </span>
              </div>
            )}
          </div>
        </div>

        <div
          className={cn(
            'shrink-0 flex items-center gap-2 overflow-hidden whitespace-nowrap [&>*]:shrink-0 transition-[max-width,opacity,padding] duration-150 ease-out motion-reduce:transition-none',
            hovered || dropdownOpen
              ? 'max-w-[240px] pl-3 opacity-100'
              : 'max-w-0 pl-0 opacity-0 pointer-events-none'
          )}
        >
          {hasDropdownActions && (
            <Dropdown
              menu={{ groups: [{ items: dropdownItems }] }}
              align="end"
              onOpenChange={setDropdownOpen}
            >
              <IconButton
                icon={
                  <Icons.DotsVertical className="size-4 text-fg-quaternary" />
                }
                className="rounded-xs p-md flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
                aria-label="Filing actions"
              />
            </Dropdown>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            View filing
          </Button>
        </div>
      </div>
      {canExtendDirectly && compliance && (
        <ExtendFilingModal
          isOpen={extendFilingModalVisibility.visible}
          onClose={extendFilingModalVisibility.hide}
          filingId={event.resource.id}
          filingTitle={title}
          currentDueDate={compliance.dueDate}
          entityName={corporationName}
          companyId={compliance.company.id}
          onSuccess={() => {
            refreshCompliance().catch(() => undefined);
            onFilingMutated?.();
          }}
        />
      )}
      {canRequestExtension && compliance && (
        <RequestExtensionModal
          isOpen={requestExtensionModalVisibility.visible}
          onClose={requestExtensionModalVisibility.hide}
          filingId={event.resource.id}
          filingTitle={title}
          entityName={corporationName}
          jurisdiction={state ?? undefined}
          category={categoryLabel}
          syntheticStatus={compliance.syntheticStatus}
          dueDate={compliance.dueDate}
          onSuccess={() => {
            refreshCompliance().catch(() => undefined);
            onFilingMutated?.();
          }}
        />
      )}
      {canArchive && (
        <ArchiveFilingModal
          isOpen={archiveFilingModalVisibility.visible}
          onClose={archiveFilingModalVisibility.hide}
          filingId={event.resource.id}
          filingTitle={title}
          entityName={corporationName}
          onSuccess={() => onFilingMutated?.()}
        />
      )}
    </div>
  );
};

export default AgendaEventCard;
